/**
 * Pure parts of the handover / return dialogs (P4-d / P4-e): the checklist's Czech vocabulary, the form state → request
 * body mapping, and the driver selection logic. Nothing here judges whether a handover is ALLOWED — that is the server's
 * `chybi` list, shown as it comes (the form never re-implements the rules).
 */

import type { PredaniVstup, Rezervace, RezervaceRidic } from './api';

export type KontrolniSeznam = NonNullable<PredaniVstup['kontrolni_seznam']>;
export type Palivo = NonNullable<KontrolniSeznam['palivo']>;
export type Cistota = NonNullable<KontrolniSeznam['cistota']>;
export type Zapach = NonNullable<KontrolniSeznam['zapach']>;
export type KauceZpusob = 'hotove' | 'karta' | 'prevod';

export const PALIVO: Array<{ value: Palivo; label: string }> = [
  { value: 'prazdna', label: 'Prázdná' },
  { value: 'ctvrtina', label: '¼ nádrže' },
  { value: 'pulka', label: '½ nádrže' },
  { value: 'tri_ctvrte', label: '¾ nádrže' },
  { value: 'plna', label: 'Plná' },
];
export const CISTOTA: Array<{ value: Cistota; label: string }> = [
  { value: 'cisty', label: 'Čisté' },
  { value: 'znecisteny', label: 'Znečištěné' },
];
export const ZAPACH: Array<{ value: Zapach; label: string }> = [
  { value: 'zadny', label: 'Bez zápachu' },
  { value: 'cigarety', label: 'Cigarety' },
  { value: 'jiny', label: 'Jiný zápach' },
];
export const KAUCE_ZPUSOB: Array<{ value: KauceZpusob; label: string }> = [
  { value: 'hotove', label: 'Hotově' },
  { value: 'karta', label: 'Kartou' },
  { value: 'prevod', label: 'Převodem' },
];

/** The five ticks of the checklist, in the order of the legacy handover form. */
export const KONTROLNI_BODY = [
  { key: 'klic', label: 'Klíče předány' },
  { key: 'maly_tp', label: 'Malý technický průkaz' },
  { key: 'zelena_karta', label: 'Zelená karta' },
  { key: 'najemni_smlouva', label: 'Nájemní smlouva' },
  { key: 'stav_potvrzen', label: 'Zákazník potvrdil stav vozu' },
] as const satisfies ReadonlyArray<{ key: keyof KontrolniSeznam; label: string }>;
export type KontrolniBod = (typeof KONTROLNI_BODY)[number]['key'];

export interface KauceForm {
  ano: boolean;
  /** Digits only, as typed. */
  castka: string;
  zpusob: KauceZpusob | '';
}

export interface PredaniForm {
  /** Digits only, as typed. */
  km: string;
  poznamka: string;
  body: Record<KontrolniBod, boolean>;
  palivo: Palivo | '';
  cistota: Cistota | '';
  zapach: Zapach | '';
  kauce: KauceForm;
  overrideDuvod: string;
}

export const PRAZDNY_FORMULAR: PredaniForm = {
  km: '',
  poznamka: '',
  body: {
    klic: false,
    maly_tp: false,
    zelena_karta: false,
    najemni_smlouva: false,
    stav_potvrzen: false,
  },
  palivo: '',
  cistota: '',
  zapach: '',
  kauce: { ano: false, castka: '', zpusob: '' },
  overrideDuvod: '',
};

export const digits = (s: string): string => s.replace(/\D/g, '');

/** The checklist as sent: the five ticks always (an unticked one is a recorded "no"), a select only when it was chosen. */
export function kontrolniSeznamZFormulare(f: PredaniForm): KontrolniSeznam {
  const seznam: KontrolniSeznam = { ...f.body };
  if (f.palivo) seznam.palivo = f.palivo;
  if (f.cistota) seznam.cistota = f.cistota;
  if (f.zapach) seznam.zapach = f.zapach;
  return seznam;
}

/** The most a protocol records as the deposit taken (the API's limit); a bigger agreed deposit is shown but not prefilled. */
export const KAUCE_PROTOKOL_MAX = 999999;

/**
 * The deposit the reservation was booked with (rent + deposit are frozen in its price), in Kč; null when it has none or no price. The dialog
 * shows it and offers it as the amount when "Kauce složena" is ticked — what is actually taken is still the employee's fact to record.
 */
export const kauceDleRezervace = (r: Pick<Rezervace, 'cena'>): number | null => (r.cena && r.cena.kauce > 0 ? r.cena.kauce : null);

/** The amount to offer when the box is ticked: the agreed deposit, unless something is typed already or it does not fit the protocol. */
export function kauceKPredvyplneni(r: Pick<Rezervace, 'cena'>, castka: string): string {
  const dohodnuta = kauceDleRezervace(r);
  return castka === '' && dohodnuta != null && dohodnuta <= KAUCE_PROTOKOL_MAX ? String(dohodnuta) : castka;
}

/**
 * What the return offers for the deposit given back: what the HANDOVER recorded as taken (amount and way), so the employee confirms or
 * corrects it instead of retyping. Nothing when the handover took no deposit or recorded no amount.
 */
export function kauceKVraceni(predani: { kauce?: { ano: boolean; castka?: number | null; zpusob?: KauceZpusob | null } | null } | undefined): Pick<KauceForm, 'castka' | 'zpusob'> {
  const k = predani?.kauce;
  if (!k?.ano) return { castka: '', zpusob: '' };
  return { castka: k.castka != null ? String(k.castka) : '', zpusob: k.zpusob ?? '' };
}

/** The deposit as sent. The checkbox is a yes / no FACT: an unchecked one is "no deposit", not "nothing recorded". */
export function kauceZFormulare(k: KauceForm): NonNullable<PredaniVstup['kauce']> {
  return k.ano
    ? { ano: true, castka: k.castka === '' ? null : Number(k.castka), zpusob: k.zpusob || null }
    : { ano: false, castka: null, zpusob: null };
}

/** The request body of a handover. A technical block sends km and the note only; the customer data belongs to a customer rental. */
export function predaniBody(
  f: PredaniForm,
  opts: { technicka: boolean; override: boolean },
): PredaniVstup {
  const base: PredaniVstup = { km: Number(f.km), poznamka: f.poznamka.trim(), override_duvod: '' };
  if (opts.technicka) return base;
  const body: PredaniVstup = { ...base, kontrolni_seznam: kontrolniSeznamZFormulare(f), kauce: kauceZFormulare(f.kauce) };
  if (opts.override) body.override_duvod = f.overrideDuvod.trim();
  return body;
}

/** A selection of drivers: ids in the order picked and the main one. */
export interface VyberRidicu {
  ids: number[];
  hlavni: number | null;
}

export const vyberZeRezervace = (ridici: RezervaceRidic[]): VyberRidicu => ({
  ids: ridici.map((r) => r.id_ridice),
  hlavni: ridici.find((r) => r.hlavni)?.id_ridice ?? null,
});

/** Tick / untick a driver. The first one ticked becomes the main driver; unticking the main one hands the role to the next. */
export function prepniRidice(v: VyberRidicu, id: number): VyberRidicu {
  if (v.ids.includes(id)) {
    const ids = v.ids.filter((x) => x !== id);
    return { ids, hlavni: v.hlavni === id ? (ids[0] ?? null) : v.hlavni };
  }
  return { ids: [...v.ids, id], hlavni: v.hlavni ?? id };
}

/** Make a ticked driver the main one. */
export const nastavHlavniho = (v: VyberRidicu, id: number): VyberRidicu =>
  v.ids.includes(id) ? { ...v, hlavni: id } : v;

/** True when the selection differs from the stored set of drivers (order does not matter). */
export function jeZmenen(v: VyberRidicu, ulozeno: VyberRidicu): boolean {
  if (v.hlavni !== ulozeno.hlavni || v.ids.length !== ulozeno.ids.length) return true;
  const a = new Set(ulozeno.ids);
  return v.ids.some((id) => !a.has(id));
}

/** The body of `PUT …/ridici`. */
export const ridiciBody = (v: VyberRidicu): Array<{ id_ridice: number; hlavni: boolean }> =>
  v.ids.map((id) => ({ id_ridice: id, hlavni: id === v.hlavni }));

/** Why the handover button is still disabled, or null. Mirrors only what the form itself controls. */
export function predaniZablokovano(opts: {
  km: string;
  zmenenyRidici: boolean;
  technicka: boolean;
  chybi: string[];
  canOverride: boolean;
  overrideDuvod: string;
}): string | null {
  if (opts.km === '') return 'Vyplňte nájezd.';
  if (opts.zmenenyRidici) return 'Nejdřív uložte vybrané řidiče.';
  if (!opts.technicka && opts.chybi.length > 0) {
    if (!opts.canOverride)
      return 'Chybí údaje k předání — doplňte je, nebo předání provede správce půjčovny.';
    if (opts.overrideDuvod.trim() === '')
      return 'Vyplňte důvod předání navzdory chybějícím údajům.';
  }
  return null;
}
